import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { ORGANIZATION_TITLE_MAX_LENGTH, ORGANIZATION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import { getWorkDomainFromEmail } from '@gitbook/app-database';
import {
    BooleanField,
    Button,
    Checkbox,
    type CheckboxProps,
    FieldError,
    Form,
    Label,
    TextField,
    TextInput,
    useFieldProps,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../navigation';
import {
    clearOnboardingParam,
    getOnboardingParam,
    OnboardingParamName,
    useLoggedInUser,
} from '../../state';
import type { PendingOrganization } from './useFlowContext';
import { getOrgInitialTitle } from './utils';

type CreateOrganizationFormInputs = {
    title: string;
    emailDomainEnabled: boolean;
};

/**
 * Screen to name the organization to create. The organization itself is not
 * created here — it's created once the site onboarding that follows is complete.
 */
export function CreateOrganizationScreen(props: {
    reason?: analytics.OrganizationUpgradeReason;

    // flow controls
    onNext: (pendingOrganization: PendingOrganization) => void;
    onTitleChange?: (title: string) => void;
}): React.ReactElement {
    const { reason, onNext, onTitleChange } = props;

    const user = useLoggedInUser();
    const emailDomain = user.email ? getWorkDomainFromEmail(user.email) : null;

    const form = useForm<CreateOrganizationFormInputs>({
        defaultValues: {
            title:
                getOnboardingParam(OnboardingParamName.ORG_NAME) ??
                getOrgInitialTitle({ email: user.email }),
            emailDomainEnabled: Boolean(emailDomain),
        },
    });

    useSetAnalyticsScreen(
        {
            screen: 'welcome_create',
            reason,
        },
        []
    );

    const onSubmit: SubmitHandler<CreateOrganizationFormInputs> = (data) => {
        clearOnboardingParam(OnboardingParamName.ORG_NAME);
        onNext({ title: data.title, emailDomainEnabled: data.emailDomainEnabled });
    };

    const title = form.watch('title');

    React.useEffect(() => {
        onTitleChange?.(title);
    }, [onTitleChange, title]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex w-full flex-col gap-4">
                <h2 className="heading-page">
                    <p className="text-base/60">Set up your organization.</p>
                    <p>Your docs live here.</p>
                </h2>
            </div>

            <div className="flex w-full flex-col gap-3">
                <TextField
                    name="title"
                    required
                    minLength={ORGANIZATION_TITLE_MIN_LENGTH}
                    maxLength={ORGANIZATION_TITLE_MAX_LENGTH}
                    validate={(value) => {
                        if (
                            value.length < ORGANIZATION_TITLE_MIN_LENGTH ||
                            value.length > ORGANIZATION_TITLE_MAX_LENGTH
                        ) {
                            return 'Enter a name between 2 and 300 characters';
                        }
                    }}
                >
                    <Label>Organization name</Label>
                    <TextInput placeholder="e.g. MyCompany" autoFocus />
                    <FieldError />
                </TextField>

                {emailDomain ? (
                    <BooleanField name="emailDomainEnabled">
                        <Label headless className="flex items-center gap-2 text-ui-base text-muted">
                            <CheckboxWithFieldProps size="small" />
                            <span>
                                Let teammates join this organization with their{' '}
                                <strong>@{emailDomain}</strong> emails
                            </span>
                        </Label>
                    </BooleanField>
                ) : null}
            </div>

            <div className="flex w-full">
                <Button
                    type="submit"
                    label="Continue"
                    kind="primary"
                    disabled={!title}
                    size="large"
                    className="w-full"
                />
            </div>
        </Form>
    );
}

/**
 * Checkbox component that properly connects to form fields using useFieldProps
 */
function CheckboxWithFieldProps(props: CheckboxProps) {
    const fieldProps = useFieldProps({});
    return (
        <Checkbox
            {...props}
            value={fieldProps.value}
            onChange={fieldProps.onChange}
            disabled={fieldProps.disabled}
        />
    );
}
